// NIST Cybersecurity Framework (CSF) 2.0 Maturity Assessment.
// 16 questions across the six CSF 2.0 Functions, each scored 1-5. The overall
// average maps to the four official CSF Implementation Tiers.
const q = (id, domain, prompt, options) => ({ id, domain, prompt, options });
const opt = (label, value) => ({ label, value });

// Standard 1-5 maturity ladder reused across most questions.
const ladder = (l1, l2, l3, l4, l5) => [
  opt(l1, 1), opt(l2, 2), opt(l3, 3), opt(l4, 4), opt(l5, 5),
];

export const nistCsfDomains = [
  'Govern (GV)',
  'Identify (ID)',
  'Protect (PR)',
  'Detect (DE)',
  'Respond (RS)',
  'Recover (RC)',
];

export const nistCsfQuestions = [
  // --- GOVERN ---
  q('gv-1', 'Govern (GV)', 'Is there a documented cybersecurity strategy and risk-management approach owned by leadership?', ladder(
    'No documented strategy; security is handled reactively',
    'An informal approach exists but is not written down or owned',
    'A documented strategy exists and is reviewed occasionally',
    'Strategy is board-approved and reviewed on a regular cadence',
    'Strategy is continuously updated and drives budget and priorities',
  )),
  q('gv-2', 'Govern (GV)', 'How are cybersecurity roles, responsibilities, and accountability defined?', ladder(
    'Roles are unclear; security is "everyone and no one"',
    'A few individuals informally cover security',
    'Key roles are documented for core activities',
    'Roles, RACI, and escalation paths are defined and understood',
    'Accountability is measured, with authority matched to responsibility',
  )),
  q('gv-3', 'Govern (GV)', 'How is cybersecurity risk to the organization identified, prioritized, and tracked?', ladder(
    'Risks are not formally tracked',
    'Risks are noted ad hoc, usually after an incident',
    'A risk register exists and is updated periodically',
    'Risks are scored, owned, and reviewed on a schedule',
    'Risk appetite is defined and actively steers decisions and reporting',
  )),

  // --- IDENTIFY ---
  q('id-1', 'Identify (ID)', 'How complete and current is your inventory of assets (hardware, software, data, cloud)?', ladder(
    'No maintained inventory',
    'Partial lists that quickly go stale',
    'Inventory maintained for compliance, updated occasionally',
    'Automated discovery keeps most assets current',
    'Continuous discovery across on-prem, cloud, and SaaS with ownership',
  )),
  q('id-2', 'Identify (ID)', 'Do you know where your sensitive and regulated data lives and how it flows?', ladder(
    'No data mapping has been done',
    'Partial understanding held by a few people',
    'Mapped for a specific compliance need',
    'Data classified and flows documented for key systems',
    'Continuous data discovery and classification tied to controls',
  )),
  q('id-3', 'Identify (ID)', 'How do you manage cybersecurity risk from third parties and suppliers?', ladder(
    'No third-party assessment',
    'We ask for a certificate and little else',
    'A security questionnaire at onboarding',
    'Risk-based due diligence scaled to data sensitivity',
    'Continuous supplier monitoring with contractual security terms',
  )),

  // --- PROTECT ---
  q('pr-1', 'Protect (PR)', 'How is identity and access managed (accounts, privilege, MFA)?', ladder(
    'Shared accounts; no MFA',
    'Individual accounts, inconsistent MFA, no reviews',
    'MFA on key systems with periodic access reviews',
    'Least-privilege enforced with MFA everywhere',
    'Just-in-time privileged access with full audit trail',
  )),
  q('pr-2', 'Protect (PR)', 'How consistently are systems hardened and patched?', ladder(
    'No standard baselines; patching is ad hoc',
    'Patching happens, but slowly and unevenly',
    'Baselines defined and patching scheduled for most systems',
    'Automated patching with SLAs and configuration standards',
    'Continuous, measured hardening with drift detection',
  )),
  q('pr-3', 'Protect (PR)', 'How is your workforce trained to recognize and resist attacks (e.g. phishing)?', ladder(
    'No security awareness training',
    'One-off training at onboarding only',
    'Annual awareness training',
    'Regular training plus simulated phishing',
    'Continuous, role-based training measured by behaviour change',
  )),

  // --- DETECT ---
  q('de-1', 'Detect (DE)', 'What level of monitoring and logging do you have across your environment?', ladder(
    'No centralized logging',
    'Logs collected but rarely reviewed',
    'Logs reviewed periodically',
    'SIEM with alerting during business hours',
    '24/7 monitoring with detections mapped to attacker techniques',
  )),
  q('de-2', 'Detect (DE)', 'How do you know your detections actually catch real attacks?', ladder(
    'We do not test detections',
    'Only reviewed after an incident',
    'Occasional manual review',
    'Regular tuning based on false-positive rate',
    'Continuous validation against realistic attack scenarios',
  )),
  q('de-3', 'Detect (DE)', 'How quickly would a malicious event realistically be detected?', ladder(
    'We might never notice',
    'Weeks',
    'Days',
    'Hours',
    'Minutes, with defined triage',
  )),

  // --- RESPOND ---
  q('rs-1', 'Respond (RS)', 'Do you have a tested incident response plan with defined roles?', ladder(
    'No plan',
    'A plan exists but has never been tested',
    'Tabletop exercise completed once',
    'Annual exercises with defined roles and runbooks',
    'Regular exercises plus post-incident reviews that update the plan',
  )),
  q('rs-2', 'Respond (RS)', 'How are incidents communicated and coordinated (internally, customers, regulators)?', ladder(
    'No communication plan',
    'Worked out during the incident',
    'Internal contacts defined',
    'Comms plan covers stakeholders and regulatory timelines',
    'Rehearsed comms with legal/PR and regulator playbooks ready',
  )),

  // --- RECOVER ---
  q('rc-1', 'Recover (RC)', 'How resilient and tested are your backups, especially against ransomware?', ladder(
    'Backups share the network/credentials of production',
    'Separate backups, but untested',
    'Restores tested occasionally',
    'Immutable or offline copy, tested regularly',
    'Full 3-2-1-1-0 strategy with defined RTO/RPO',
  )),
  q('rc-2', 'Recover (RC)', 'Could you restore critical operations within a defined, acceptable timeframe?', ladder(
    'No recovery objectives defined',
    'We hope so, but have never proven it',
    'RTO/RPO defined for a few systems',
    'Recovery tested for critical systems against targets',
    'Recovery continuously validated and improved after each test',
  )),
];

export const nistCsfMeta = {
  eyebrow: 'Free Tool · NIST CSF 2.0',
  heroTitle: 'NIST CSF 2.0 Maturity Assessment',
  heroDesc:
    'Sixteen questions across the six NIST Cybersecurity Framework 2.0 Functions — Govern, Identify, Protect, Detect, Respond, and Recover. About five minutes, and you get an indicative Implementation Tier plus a per-function breakdown of where to focus first.',
  resultsIntro: 'Your Indicative CSF Implementation Tier',
  domainLabel: 'CSF Function',
  frameworkShort: 'NIST CSF program',
  source: 'NIST CSF 2.0 Assessment',
  disclaimer:
    'This Implementation Tier is indicative only — it is estimated from the answers you provided in this short self-assessment, not from a review of your systems or evidence. It is not a formal NIST CSF assessment, audit, or certification, and a hands-on evaluation by our team may reach a different conclusion. Treat it as a starting point for the conversation, not a final result.',
};

const csfTier = (avg) => {
  if (avg < 1.75) return {
    tier: 'Tier 1 — Partial',
    desc: 'Cybersecurity risk is managed in an ad hoc, largely reactive way, with limited awareness at the organizational level. This is a common starting point — the biggest gains come from establishing governance and basic, repeatable practices rather than buying more tools.',
  };
  if (avg < 2.75) return {
    tier: 'Tier 2 — Risk Informed',
    desc: 'Risk-management practices are approved by management but may not be established organization-wide. There is awareness, but consistency and prioritization across functions are the next step-change.',
  };
  if (avg < 3.75) return {
    tier: 'Tier 3 — Repeatable',
    desc: 'You have formal policies and repeatable practices that are updated based on changes in risk. The highest-value work now is testing those practices under realistic conditions and closing the gaps between functions.',
  };
  return {
    tier: 'Tier 4 — Adaptive',
    desc: 'Cybersecurity practices are continuously improved from lessons learned and predictive indicators, and risk-informed decisions are part of the culture. Focus shifts to continuous validation, threat-led testing, and staying ahead of change.',
  };
};

export const computeNistCsfResult = (answers, questions) => {
  const byDomain = {};
  questions.forEach((ques) => {
    const val = answers[ques.id] ?? 1;
    (byDomain[ques.domain] = byDomain[ques.domain] || []).push(val);
  });
  const domainScores = Object.entries(byDomain).map(([domain, vals]) => ({
    domain,
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
  }));
  const overallAvg = domainScores.reduce((s, d) => s + d.avg, 0) / domainScores.length;
  const { tier, desc } = csfTier(overallAvg);
  const weakest = [...domainScores].sort((a, b) => a.avg - b.avg)[0];

  return {
    overallAvg,
    overallPct: Math.round((overallAvg / 5) * 100),
    tier,
    tierDesc: desc,
    domainScores,
    weakestDomain: weakest.domain,
    ctaHref: '/services/nist-csf-implementation',
    ctaLabel: 'See NIST CSF 2.0 Implementation',
  };
};
